import React, { useEffect, useState } from "react";
import { useReportAutoRun } from "hooks/useReportAutoRun/useReportAutoRun";
import { useLocation } from "react-router-dom";

import { downloadCsv, formatDate, openPrintWindow } from "helpers/Helpers";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH, TABLET_WIDTH } from "helpers/ScreenSizes";

import { GetReport } from "api/rpc/2024-04/facilityAdmin/report/report";
import { StatusCode } from "api/protocols";

import Page from "components/page/Page";
import Card from "components/card/Card";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import { useTranslation } from "react-i18next";
import "../report.scss";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import StaffAccountShiftsTable from "../tables/StaffAccountShiftsTable";
import FilterDropdown from "components/filterDropDown/FilterDropDown";
import axios, { CancelToken } from "axios";
import { IFacilityStaff } from "redux/reducers/models/facility";
import { GetFacilityAdmin } from "api/rpc/2024-04/facilityAdmin/client/admin/admin";
import moment from "moment";
import ReportEmailModal from "components/reportEmailModal/ReportEmailModal";
import Checkbox from "components/form/checkbox/Checkbox";
import { useReportRun } from "../useReportRun";

interface IReportState {
  id: number;
  title: string;
  code: string;
  dateRangeStart: Date;
  dateRangeEnd: Date;
  singleDate: Date;
  date_range: Array<string>;
  date: string;
  facilityOptions: Array<{ label: string; onClick: () => void }>;
  facility_id_name: string;
  user_ids: Array<number>;
  email: string;
  queue_after_days: number;
  totalsRowsOnly: boolean;
  [key: string]: any;
}

interface IReportDataState {
  headings: any;
  rows: any;
  columns: Array<Record<string, any>>;
  tableDisplay: boolean;
  description: string;
  compileDate: string;
}

export default function StaffAccountShifts() {
  const windowSize = useWindowSize();
  const location = useLocation();
  const GetReportRun = useReportRun();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const routeName = location.pathname.split("/");
  const code = routeName[routeName.length - 1];
  const [adminData, setAdminData] = useState<IFacilityStaff[]>(undefined);
  const [reportEmail, setReportEmail] = useState({
    handler: null,
    open: false,
  });

  const { authStore } = useAppSelector(store => store);


  function formatDateRange(startingDate: Date, endingDate: Date) {
    return [formatDate(startingDate), formatDate(endingDate) + " 23:59:59"];
  }

  const today = new Date();

  const [state, setState] = useState<IReportState>({
    dateRangeStart: today,
    dateRangeEnd: today,
    singleDate: today,
    id: null,
    title: "",
    code: "",
    date_range: formatDateRange(today, today),
    date: formatDate(today),
    facilityOptions: [],
    facility_id_name: "",
    user_ids: null,
    email: "",
    queue_after_days: 14,
    totalsRowsOnly: false,
  });

  const [reportData, setReportData] = useState<IReportDataState>({
    headings: [],
    rows: [],
    columns: [],
    tableDisplay: false,
    description: "",
    compileDate: "",
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadReport(source.token);
    void loadFacilityStaff(source.token);
    return () => source.cancel();
  }, []);

  useReportAutoRun({
    isReady: state.id != null,
    reportId: state.id,
    setState,
    run: () => {
      void getReport(state.email);
    },
  });

  useEffect(() => {
    if (reportEmail.open === false && reportEmail.handler !== null) {
      reportEmail.handler(state.email);
    }
  }, [reportEmail.open]);

  async function loadFacilityStaff(token?: CancelToken) {
    if (adminData !== undefined) {
      setAdminData(undefined);
    }

    const res = await GetFacilityAdmin(null, token ? false : true, token);
    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading staff accounts for the facility.")));
    }

    setAdminData(res.status !== StatusCode.OK ? [] : res.data);
  }

  async function loadReport(token?: CancelToken) {
    const reportRes = await GetReport({ code: code }, true, token);
    if (reportRes.status !== StatusCode.OK) {
      return;
    }

    const report = reportRes.data[0];

    setState(prev => ({
      ...prev,
      id: report.id,
      title: report.title,
      code: report.code,
    }));
  }

  async function getReport(email: string) {
    const reportRes = await GetReportRun(
      {
        report_id: state.id,
        date_range: state.date_range,
        user_ids: state.user_ids,
        email: email,
        totals_only: state?.totalsRowsOnly,
        admin_view_shift_wages: authStore?.user?.permissions?.admin_view_shift_wages,
      },
      true,
    );

    if (reportRes.status !== StatusCode.OK) {
      dispatch(showError(reportRes.message));
      return;
    }

    if (reportRes.message) {
      dispatch(showSuccess(reportRes.message));
    }

    const compileDate = reportRes.data?.compiled_at ? reportRes.data?.compiled_at.concat("Z") : "";

    setReportData(prev => ({
      ...prev,
      rows: reportRes.data.rows,
      columns: reportRes.data.columns,
      description: reportRes.data?.description ?? "",
      compileDate,
    }));

    return;
  }

  async function getDownload(email: string) {
    const segmentsRes = await GetReportRun(
      {
        report_id: state.id,
        type: "csv",
        date_range: state.date_range,
        user_ids: state.user_ids,
        email,
        totals_only: state?.totalsRowsOnly,
        admin_view_shift_wages: authStore?.user?.permissions?.admin_view_shift_wages,
      },
      true,
    );

    if (segmentsRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error downloading report")));
      return;
    }

    if (!email) {
      downloadCsv(segmentsRes.data as string, reportFilename() + '.csv');
    } else {
      dispatch(showSuccess(segmentsRes.message));
    }
  }

  async function getPrint(email: string) {
    const printRes = await GetReportRun(
      {
        report_id: state.id,
        type: "print",
        date_range: state.date_range,
        user_ids: state.user_ids,
        email: "",
        totals_only: state?.totalsRowsOnly,
        admin_view_shift_wages: authStore?.user?.permissions?.admin_view_shift_wages,
      },
      true,
    );

    if (printRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error running report")));
      return;
    }

    if (!email) {
      openPrintWindow(printRes.data as string);
    } else {
      dispatch(showSuccess(printRes.message));
    }
  }

  useEffect(() => {
    if (state.dateRangeStart && state.dateRangeEnd) {
      const date_range = formatDateRange(state.dateRangeStart, state.dateRangeEnd);
      setState(prevState => ({ ...prevState, date_range }));
    }
  }, [state.dateRangeStart, state.dateRangeEnd]);

  function setDateRangeStart(date: Date) {
    setState(prevState => ({
      ...prevState,
      dateRangeStart: date,
    }));
  }

  function setDateRangeEnd(date: Date) {
    setState(prevState => ({
      ...prevState,
      dateRangeEnd: date,
    }));
  }

  function onCheckBoxChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { value, id, checked } = e.target;
    let updateSelectedValues: number[] = state[id] ? [...state[id]] : [];
    if (checked) {
      updateSelectedValues.push(Number(value));
    } else {
      updateSelectedValues = updateSelectedValues?.filter(id => id !== Number(value));
    }
    setState(prevState => ({ ...prevState, [id]: updateSelectedValues }));
  }

  function handleAddAllStaff() {
    const allStaffIds = adminData ? adminData?.map(staff => staff?.id) : [];
    setState(prevState => ({ ...prevState, user_ids: allStaffIds }));
  }

  console.log('%csrc/pages/secure/facility/reports/pages/StaffAccountShifts.tsx:251 state', 'color: #007acc;', state);

  function reportShouldQueue(): boolean {
    return moment(state.dateRangeEnd).diff(moment(state.dateRangeStart), 'days') >= state.queue_after_days;
  }

  function handleReportEmailSelected(email: string) {
    setState({ ...state, email });
    setReportEmail({
      ...reportEmail,
      open: false,
    });
  }

  function handleReportEmailClose() {
    setReportEmail({ handler: null, open: false });
  }

  function handleDownload() {
    if (reportShouldQueue()) {
      setReportEmail({
        open: true,
        handler: getDownload,
      });
    } else {
      void getDownload("");
    }
  }

  function handlePrint() {
    if (reportShouldQueue()) {
      setReportEmail({
        open: true,
        handler: getPrint,
      });
    } else {
      void getPrint("");
    }
  }

  function reportFilename(): string {
    return `${state.title}_${moment(new Date()).format("YYYY-MM-DD").toString()}`
      .replace(/ /g, "_")
      .toLowerCase();
  }

  const primaryAction = {
    content: t("Run"),
    action: () => {
      if (reportShouldQueue()) {
        dispatch(showSuccess(
          t('Potential large report detected.'),
          10000,
          t('An email is required to send the report to upon completion.'),
        ));

        setReportEmail({
          open: true,
          handler: getReport,
        });
      } else {
        void getReport(state.email);
      }
    },
  };

  return (
    <Page
      title={t("Staff Account Shifts")}
      primaryAction={primaryAction}
      multipleActionDropdownAction={{
        label: t("Options"),
        dropdownProps: {
          alignment: "right",
          options: [
            {
              type: "handler",
              label: t("Export"),
              handler: () => handleDownload(),
              icon: "file-arrow-down",
            },
            {
              type: "handler",
              label: t("Print"),
              handler: () => handlePrint(),
              icon: "print",
            },
          ],
        },
      }}
      breadcrumbs={[{ prefix: true, label: t("Back to Reports"), url: "/admin/report" }]}
    >
      <Card>
        <Card.Section>
          <div className="report-filters-container">
            <DatePickerInput
              months={windowSize.width > TABLET_WIDTH ? 2 : 1}
              position="left"
              showQuickOptions={windowSize.width > MOBILE_WIDTH}
              startingDate={state.dateRangeStart}
              setStartingDate={setDateRangeStart}
              endingDate={state.dateRangeEnd}
              setEndingDate={setDateRangeEnd}
            />
            <FilterDropdown
              label={t("Staff")}
              data={adminData}
              dataLabelProperty={"full_name"}
              checkBoxId={"user_ids"}
              dataValueProperty="id"
              onCheckBoxChange={onCheckBoxChange}
              onClear={() => setState(prevState => ({ ...prevState, user_ids: null }))}
              onAddAll={() => handleAddAllStaff()}
              leftAligned={windowSize.width <= MOBILE_WIDTH}
            />
            <Checkbox
              label={t("Show total hours per staff member")}
              checked={state.totalsRowsOnly}
              onChange={e => setState(prev => ({ ...prev, totalsRowsOnly: e.target.checked }))}
              size="medium"
              supportText={t("Individual shifts are hidden and only the total hours worked by each staff member during the selected date range are shown")}
            />
          </div>
        </Card.Section>
      </Card>

      <div className="dynamic-table-container">
        <StaffAccountShiftsTable
          data={reportData.rows}
          columns={reportData.columns}
          description={reportData.description}
          compileDate={reportData.compileDate}
        />
      </div>
      <ReportEmailModal
        open={reportEmail.open}
        onEmailSelected={handleReportEmailSelected}
        onClose={handleReportEmailClose}
      />
    </Page>
  );
}
